/**
 * The puja catalogue — `/v1/admin/pujas*` and `/v1/admin/puja-categories*`.
 *
 * **Every word a devotee reads about a ritual is a row here**, which is why this module is a thin
 * pass-through and holds no rules of its own. Two of the server's rules are worth knowing while
 * reading it, because the screen surfaces them rather than reimplementing them.
 *
 * **The content lint runs on the server, on every write.** No outcome claim — health, wealth,
 * jobs, court cases, marriage, fertility, "removes dosh" — reaches a published page, and the
 * refusal comes back as a 422 naming the rule. A client-side check would be a second copy that
 * drifts, and the one that matters is the one the deploy runs.
 *
 * **A published puja's slug is frozen** (`slug_frozen`). Changing it would break every inbound link
 * and every search result already pointing at the page, so the server refuses; the screen shows the
 * flag rather than letting somebody type into a field that will be rejected.
 *
 * **Archiving is soft.** A puja with skills and bookings pointing at it keeps them — it drops out
 * of pickers and matching, and `restore` puts it back.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type PujaRow = components['schemas']['AdminPujaRow'];
export type PujaDetail = components['schemas']['AdminPujaDetail'];
export type CategoryRow = components['schemas']['AdminCategoryRow'];
type PujaList = components['schemas']['AdminPujaListResponse'];

export interface PujaQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  is_active?: boolean;
  include_archived?: boolean;
}

/** Exported for its own test: `is_active: false` is a filter, `undefined` is the absence of one. */
export function pujaQueryString(params: PujaQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.is_active !== undefined) search.set('is_active', String(params.is_active));
  if (params.include_archived !== undefined) {
    search.set('include_archived', String(params.include_archived));
  }
  return search.toString();
}

export function usePujas(params: PujaQuery) {
  return useQuery({
    queryKey: keys.catalogue.pujas(params),
    queryFn: () => authedClient.request<PujaList>(`/v1/admin/pujas?${pujaQueryString(params)}`),
  });
}

export function usePuja(id: number | undefined) {
  return useQuery({
    queryKey: keys.catalogue.puja(id ?? 0),
    queryFn: () => authedClient.request<PujaDetail>(`/v1/admin/pujas/${id}`),
    enabled: id !== undefined,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: keys.catalogue.categories(),
    queryFn: () => authedClient.request<CategoryRow[]>('/v1/admin/puja-categories'),
  });
}

function useCatalogueMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.catalogue.all() }),
  });
}

type PujaCreateIn = components['schemas']['PujaCreateIn'];

/** A new puja starts inactive unless told otherwise; it is filled in on the editor it opens into. */
export function useCreatePuja() {
  const client = useQueryClient();
  return useMutation<PujaDetail, unknown, PujaCreateIn>({
    mutationFn: (body) => authedClient.request<PujaDetail>('/v1/admin/pujas', { method: 'POST', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.catalogue.all() }),
  });
}

export function usePatchPuja() {
  return useCatalogueMutation(
    ({ pujaId, patch }: { pujaId: number; patch: components['schemas']['PujaPatchIn'] }) =>
      authedClient.request<PujaDetail>(`/v1/admin/pujas/${pujaId}`, { method: 'PATCH', body: patch }),
  );
}

export function useArchivePuja() {
  return useCatalogueMutation(({ pujaId, reason }: { pujaId: number; reason: string }) =>
    authedClient.request<void>(`/v1/admin/pujas/${pujaId}`, { method: 'DELETE', body: { reason } }),
  );
}

/**
 * Replace one of a puja's eleven sub-lists.
 *
 * Every one of these is a **whole-list replace** — the array sent becomes the list, and anything
 * omitted is gone. That is safe now only because `AdminPujaDetail` returns the same lists in the
 * same shapes (CSR-027): the console edits what it read rather than composing a body blind.
 *
 * Array position becomes `sort_order` on the server, so reordering is done by reordering.
 */
export type SubList =
  | 'steps'
  | 'faqs'
  | 'media'
  | 'modes'
  | 'deities'
  | 'occasions'
  | 'sampradayas'
  | 'related'
  | 'booking-fields'
  | 'evidence-requirements'
  | 'prep-items'
  | 'provisions';

export function useReplaceSubList() {
  return useCatalogueMutation(
    ({ pujaId, list, items }: { pujaId: number; list: SubList; items: unknown[] }) =>
      authedClient.request<components['schemas']['ReplacedResponse']>(
        `/v1/admin/pujas/${pujaId}/${list}`,
        { method: 'PUT', body: { items } },
      ),
  );
}

/** A Hindi reader approves the puja's Hindi as it stands (12-content/13 §6.2). The server refuses
 *  the admin who made the last Hindi edit; any later Hindi edit clears the approval. */
export function useApprovePujaHindi() {
  return useCatalogueMutation(({ pujaId }: { pujaId: number }) =>
    authedClient.request<components['schemas']['PujaHindiReview']>(`/v1/admin/pujas/${pujaId}/hindi-review`, {
      method: 'POST',
    }),
  );
}

export function useRestorePuja() {
  return useCatalogueMutation(({ pujaId }: { pujaId: number }) =>
    authedClient.request<PujaRow>(`/v1/admin/pujas/${pujaId}/restore`, { method: 'POST' }),
  );
}

/**
 * The four classification axes, now editable (CSR-028 closed).
 *
 * The admin read is deliberately not the public one. `/v1/taxonomy` serves active values for the
 * website's filter chips; this serves **retired values too**, because an operator who retired
 * something needs to see it in order to restore it. Different readers, different defaults.
 *
 * `name_second` is `name_hi` on three axes and `name_native` on ritual languages — Maithili is not
 * Hindi. The API normalises the field so one component renders all four, without pretending a
 * native name is a Hindi one.
 */
export type TaxonomyValue = components['schemas']['TaxonomyValueRow'];

export const TAXONOMY_AXES = ['occasions', 'deities', 'sampradayas', 'ritual-languages'] as const;
export type TaxonomyAxis = (typeof TAXONOMY_AXES)[number];

export function useAxisValues(axis: TaxonomyAxis) {
  return useQuery({
    queryKey: keys.catalogue.axis(axis),
    queryFn: () =>
      authedClient.request<components['schemas']['TaxonomyValueList']>(
        `/v1/admin/taxonomy/${axis}`,
      ),
  });
}

export function useCreateAxisValue() {
  return useCatalogueMutation(
    ({ axis, body }: { axis: TaxonomyAxis; body: components['schemas']['TaxonomyValueCreateIn'] }) =>
      authedClient.request<TaxonomyValue>(`/v1/admin/taxonomy/${axis}`, { method: 'POST', body }),
  );
}

export function usePatchAxisValue() {
  return useCatalogueMutation(
    ({
      axis,
      valueId,
      patch,
    }: {
      axis: TaxonomyAxis;
      valueId: number;
      patch: components['schemas']['TaxonomyValuePatchIn'];
    }) =>
      authedClient.request<TaxonomyValue>(`/v1/admin/taxonomy/${axis}/${valueId}`, {
        method: 'PATCH',
        body: patch,
      }),
  );
}

export function useCreateCategory() {
  return useCatalogueMutation((body: components['schemas']['CategoryIn']) =>
    authedClient.request<CategoryRow>('/v1/admin/puja-categories', { method: 'POST', body }),
  );
}

export function usePatchCategory() {
  return useCatalogueMutation(
    ({ categoryId, patch }: { categoryId: number; patch: components['schemas']['CategoryPatchIn'] }) =>
      authedClient.request<CategoryRow>(`/v1/admin/puja-categories/${categoryId}`, {
        method: 'PATCH',
        body: patch,
      }),
  );
}

export type CityAvailabilityRow = components['schemas']['CityAvailabilityRow'];
type CityAvailabilityList = components['schemas']['CityAvailabilityList'];

/**
 * The puja's per-city exceptions to its national switch (R215, 03-data/08 §3.14). Only the
 * exceptions: a city with no row follows the puja's own switch, and the server resolves that —
 * this module never recomputes whether a city is bookable.
 */
export function useCityAvailability(pujaId: number) {
  return useQuery({
    queryKey: keys.catalogue.cityAvailability(pujaId),
    queryFn: () =>
      authedClient.request<CityAvailabilityList>(`/v1/admin/pujas/${pujaId}/city-availability`),
  });
}

/** Open or close one city for this puja, with the audited reason; `note` is ops' own, never shown. */
export function useSetCityAvailability() {
  return useCatalogueMutation(
    ({ pujaId, cityId, ...body }: { pujaId: number; cityId: number; is_bookable: boolean; note?: string; reason: string }) =>
      authedClient.request<CityAvailabilityRow>(`/v1/admin/pujas/${pujaId}/city-availability/${cityId}`, {
        method: 'PUT',
        body,
      }),
  );
}

/** Drop the exception, so the city follows the national switch again. */
export function useClearCityAvailability() {
  return useCatalogueMutation(({ pujaId, cityId, reason }: { pujaId: number; cityId: number; reason: string }) =>
    authedClient.request<void>(`/v1/admin/pujas/${pujaId}/city-availability/${cityId}`, {
      method: 'DELETE',
      body: { reason },
    }),
  );
}
